import React from 'react'
import { Formik, Form, Field } from 'formik'
import { Button, Columns, Element } from 'react-bulma-components'
import OptimizedImage from '../optimized-image/optimized-image'
import { Translate } from '../../utils/translations'
import { campaignApi } from '../../campaigns/api'
import type { QuantityStepProps } from './quantity-step-props'

type CrankQuantityFormProps = QuantityStepProps

/**
 * Formulaire pour la saisie des quantités de paniers selon le type de manivelle
 * Affiche deux images (rouge et noire) et deux champs numériques obligatoires
 */
const CrankQuantityForm: React.FC<CrankQuantityFormProps> = ({
  onSuccess,
  requestor,
  language,
  savedValues,
  onSaveValues,
}) => {
  const useCreateQuantity = campaignApi.useCreateQuantity
  const crankQuantityMutation = useCreateQuantity({
    mutation: {
      onSuccess: data => {
        onSuccess(data.data)
      },
      onError: error => {
        console.error('error', error)
      },
    },
  })

  const handleSubmit = async (values: { redCrankQty: number; blackCrankQty: number }) => {
    // Persist the raw values so the step is pre-filled when navigating back.
    onSaveValues?.(values)
    await crankQuantityMutation.mutateAsync({
      data: {
        requestor: {
          country: requestor.country,
          email: requestor.email,
          language,
        },
        quantity: {
          red: Number(values.redCrankQty),
          black: Number(values.blackCrankQty),
        },
      },
    })
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        redCrankQty: savedValues?.redCrankQty,
        blackCrankQty: savedValues?.blackCrankQty,
      }}
      validate={values => {
        const errors: any = {}
        if (
          values.redCrankQty === undefined ||
          isNaN(Number(values.redCrankQty)) ||
          Number(values.redCrankQty) < 0
        ) {
          errors.redCrankQty = <Translate value="fields.required" />
        }
        if (
          values.blackCrankQty === undefined ||
          isNaN(Number(values.blackCrankQty)) ||
          Number(values.blackCrankQty) < 0
        ) {
          errors.blackCrankQty = <Translate value="fields.required" />
        }
        return errors
      }}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form>
          <Columns>
            <Columns.Column size={6} style={{ textAlign: 'center' }}>
              <OptimizedImage
                imageName="red_crank"
                alt="Panier de basket avec manivelle rouge amovible"
                priority
              />
              <Element mt={2} mb={2} textWeight="bold">
                <Translate value="recall.descriptionRed" />
              </Element>
              <div style={{ maxWidth: 200, margin: 'auto' }}>
                <label htmlFor="redCrankQty" style={{ fontWeight: 600 }}>
                  <Translate value="recall.quantity" />
                </label>
                <Field
                  id="redCrankQty"
                  name="redCrankQty"
                  type="number"
                  min={0}
                  className="input"
                  placeholder="..."
                />
                {errors.redCrankQty && touched.redCrankQty && (
                  <Element textColor="danger" mt={1} style={{ fontSize: 14 }}>
                    {errors.redCrankQty}
                  </Element>
                )}
              </div>
            </Columns.Column>
            <Columns.Column size={6} style={{ textAlign: 'center' }}>
              <OptimizedImage
                imageName="black_crank"
                alt="Panier de basket avec manivelle noire fixe"
                priority
              />
              <Element mt={2} mb={2} textWeight="bold">
                <Translate value="recall.descriptionBlack" />
              </Element>
              <div style={{ maxWidth: 200, margin: 'auto' }}>
                <label htmlFor="blackCrankQty" style={{ fontWeight: 600 }}>
                  <Translate value="recall.quantity" />
                </label>
                <Field
                  id="blackCrankQty"
                  name="blackCrankQty"
                  type="number"
                  min={0}
                  className="input"
                  placeholder="..."
                />
                {errors.blackCrankQty && touched.blackCrankQty && (
                  <Element textColor="danger" mt={1} style={{ fontSize: 14 }}>
                    {errors.blackCrankQty}
                  </Element>
                )}
              </div>
            </Columns.Column>
          </Columns>
          <Element mt={4} textAlign="center">
            <Button color="primary" type="submit" disabled={isSubmitting}>
              <Translate value="form.submit" />
            </Button>
          </Element>
        </Form>
      )}
    </Formik>
  )
}

export default CrankQuantityForm
